/**
 * usePulsePhase — fixed-clock animation phase counter for inline-SVG primitives.
 *
 * Returns an integer phase in `[0, stepCount)` that increments on a
 * `periodMs` interval and wraps. Used by the recursion-scheme and
 * operad/cooperad explainer diagrams to coordinate gentle, deterministic
 * animations without depending on `requestAnimationFrame`.
 *
 * Centralizing the hook here means future changes — pausing offscreen,
 * honoring `prefers-reduced-motion`, throttling under load — only need to
 * happen in one place.
 *
 * The hook clamps the returned phase to `[0, normalizedStepCount)` on
 * every render rather than only on each tick. That way a parent that
 * shrinks `stepCount` between renders never observes a phase outside the
 * advertised range — even before the next interval fires.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useEffect, useState } from "react";

export function usePulsePhase(stepCount: number, periodMs: number): number {
  const normalizedStepCount =
    Number.isFinite(stepCount) && stepCount >= 1 ? Math.floor(stepCount) : 1;
  const normalizedPeriodMs = Number.isFinite(periodMs) && periodMs >= 1 ? Math.floor(periodMs) : 1;
  const [phase, setPhase] = useState<number>(0);
  useEffect((): (() => void) => {
    // Reset the stored phase to 0 whenever the step count changes so a
    // stale large phase cannot survive a stepCount decrease.
    setPhase(0);
    const timer = window.setInterval((): void => {
      setPhase((value) => (value + 1) % normalizedStepCount);
    }, normalizedPeriodMs);
    return (): void => {
      window.clearInterval(timer);
    };
  }, [normalizedPeriodMs, normalizedStepCount]);
  // Belt and braces: even in the render that runs before the reset
  // effect commits, the returned phase is clamped into range.
  return phase % normalizedStepCount;
}
